import { STAFF_OR_ABOVE, UserRole, UserStatus } from "@excel-center/shared";
import { compressImage, presets } from "@/src/common/media";
import { supabase } from "@/src/common/services/supabase.service";
import { listAccessibleSchools } from "@/src/dashboard/services/dashboard.service";
import { MESSAGING_BUCKET, MESSAGING_SIGNED_URL_TTL } from "@/src/messaging/constants/messaging.constants";
import type {
	GroupOption,
	InboxMessage,
	InboxPerson,
	MessageAttachment,
	PersonPickerOption,
	Recipient,
	SchoolOption,
	SendMessageInput,
	SendMessageResult,
	SendMessageSkipped,
} from "@/src/messaging/types/messaging.types";

type MessagePersonRow = {
	id: string;
	first_name: string | null;
	last_name: string | null;
	email: string;
	role: UserRole;
	avatar_url: string | null;
};

type MessageJoinRow = {
	id: string;
	thread_id: string;
	reply_to_id: string | null;
	subject: string;
	body: string;
	created_at: string;
	read_at: string | null;
	deleted_at: string | null;
	school_id: string | null;
	sender: MessagePersonRow | null;
	recipient: MessagePersonRow | null;
};

const MESSAGE_SELECT = `
	id,
	thread_id,
	reply_to_id,
	subject,
	body,
	created_at,
	read_at,
	deleted_at,
	school_id,
	sender:profiles!messages_sender_id_fkey ( id, first_name, last_name, email, role, avatar_url ),
	recipient:profiles!messages_recipient_id_fkey ( id, first_name, last_name, email, role, avatar_url )
`;

function toPerson(row: MessagePersonRow | null): InboxPerson {
	return {
		id: row?.id ?? "",
		firstName: row?.first_name ?? null,
		lastName: row?.last_name ?? null,
		email: row?.email ?? "",
		role: row?.role ?? UserRole.STUDENT,
		avatarUrl: row?.avatar_url ?? null,
	};
}

function toInboxMessage(row: MessageJoinRow): InboxMessage {
	return {
		id: row.id,
		threadId: row.thread_id,
		replyToId: row.reply_to_id,
		subject: row.subject,
		body: row.body,
		createdAt: row.created_at,
		readAt: row.read_at,
		deletedAt: row.deleted_at,
		schoolId: row.school_id,
		sender: toPerson(row.sender),
		recipient: toPerson(row.recipient),
	};
}

function personLabel(first: string | null, last: string | null, email: string): string {
	const name = [first, last].filter(Boolean).join(" ").trim();
	return name || email;
}

function sanitizeFilename(name: string): string {
	return name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 100);
}

function buildObjectPath(file: File): string {
	const id = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : Date.now().toString(36);
	return `${id}/${sanitizeFilename(file.name || "attachment")}`;
}

function toWirePayload(recipient: Recipient): Record<string, string> {
	switch (recipient.kind) {
		case "user":
			return { kind: "user", profileId: recipient.profileId };
		case "group":
			return { kind: "group", groupId: recipient.groupId };
		case "school":
			return { kind: "school", schoolId: recipient.schoolId };
		case "email":
			return { kind: "email", email: recipient.email };
	}
}

export const messagingService = {
	async uploadAttachment(file: File): Promise<MessageAttachment> {
		const path = buildObjectPath(file);
		const uploadFile = file.type.startsWith("image/") ? (await compressImage(file, presets.messagingImage)).file : file;
		const { error: uploadError } = await supabase.storage.from(MESSAGING_BUCKET).upload(path, uploadFile, {
			upsert: false,
			contentType: uploadFile.type || undefined,
		});
		if (uploadError) throw new Error(uploadError.message);

		const { data, error: signError } = await supabase.storage.from(MESSAGING_BUCKET).createSignedUrl(path, MESSAGING_SIGNED_URL_TTL);
		if (signError || !data?.signedUrl) throw new Error(signError?.message ?? "Failed to sign attachment URL.");

		return {
			name: file.name,
			path,
			signedUrl: data.signedUrl,
			size: uploadFile.size,
			mimeType: uploadFile.type || "application/octet-stream",
		};
	},

	async removeAttachment(path: string): Promise<void> {
		await supabase.storage.from(MESSAGING_BUCKET).remove([path]);
	},

	async listGroups(): Promise<GroupOption[]> {
		const { data, error } = await supabase.from("groups").select("id, name, school_id, school:schools(name)").order("name");
		if (error) throw new Error(error.message);
		return (data ?? []).map((row) => ({
			id: row.id,
			name: row.name,
			schoolId: row.school_id,
			schoolName: (row.school as { name: string | null } | null)?.name ?? null,
		}));
	},

	async listSchools(): Promise<SchoolOption[]> {
		const rows = await listAccessibleSchools();
		return rows.map((r) => ({ id: r.id, name: r.name }));
	},

	async sendMessage(input: SendMessageInput): Promise<SendMessageResult> {
		const payload = {
			recipients: input.recipients.map(toWirePayload),
			subject: input.subject,
			body: input.body,
			attachments: input.attachments.map((a) => ({ name: a.name, signedUrl: a.signedUrl })),
			replyToMessageId: input.replyToMessageId,
		};
		const { data, error } = await supabase.functions.invoke<{
			ok: boolean;
			inserted?: number;
			emailedOnly?: number;
			skipped?: SendMessageSkipped[];
			stubbed?: boolean;
			error?: string;
		}>("send-message", { body: payload });
		if (error) throw new Error(error.message);
		if (!data?.ok) throw new Error(data?.error ?? "Message send failed.");
		return {
			ok: true,
			inserted: data.inserted ?? 0,
			emailedOnly: data.emailedOnly ?? 0,
			skipped: data.skipped ?? [],
			stubbed: data.stubbed,
		};
	},

	async getMyStudentSchoolIds(profileId: string): Promise<string[]> {
		const { data: prof, error: profErr } = await supabase.from("profiles").select("student_id").eq("id", profileId).maybeSingle();
		if (profErr) throw new Error(profErr.message);
		if (!prof?.student_id) return [];
		const { data: student, error: studentErr } = await supabase
			.from("students")
			.select("school_id")
			.eq("id", prof.student_id)
			.maybeSingle();
		if (studentErr) throw new Error(studentErr.message);
		return student?.school_id ? [student.school_id] : [];
	},

	async listInbox(userId: string): Promise<InboxMessage[]> {
		const { data, error } = await supabase
			.from("messages")
			.select(MESSAGE_SELECT)
			.eq("recipient_id", userId)
			.is("deleted_at", null)
			.order("created_at", { ascending: false })
			.overrideTypes<MessageJoinRow[], { merge: false }>();
		if (error) throw new Error(error.message);
		return (data ?? []).map(toInboxMessage);
	},

	async listSent(userId: string): Promise<InboxMessage[]> {
		const { data, error } = await supabase
			.from("messages")
			.select(MESSAGE_SELECT)
			.eq("sender_id", userId)
			.order("created_at", { ascending: false })
			.overrideTypes<MessageJoinRow[], { merge: false }>();
		if (error) throw new Error(error.message);
		return (data ?? []).map(toInboxMessage);
	},

	async listDeleted(userId: string): Promise<InboxMessage[]> {
		const { data, error } = await supabase
			.from("messages")
			.select(MESSAGE_SELECT)
			.eq("recipient_id", userId)
			.not("deleted_at", "is", null)
			.order("deleted_at", { ascending: false })
			.overrideTypes<MessageJoinRow[], { merge: false }>();
		if (error) throw new Error(error.message);
		return (data ?? []).map(toInboxMessage);
	},

	async getThread(threadId: string, userId: string): Promise<InboxMessage[]> {
		const { data, error } = await supabase
			.from("messages")
			.select(MESSAGE_SELECT)
			.eq("thread_id", threadId)
			.or(`deleted_at.is.null,sender_id.eq.${userId}`)
			.order("created_at", { ascending: true })
			.overrideTypes<MessageJoinRow[], { merge: false }>();
		if (error) throw new Error(error.message);
		return (data ?? []).map(toInboxMessage);
	},

	async markThreadRead(threadId: string, userId: string): Promise<void> {
		const { error } = await supabase
			.from("messages")
			.update({ read_at: new Date().toISOString() })
			.eq("thread_id", threadId)
			.eq("recipient_id", userId)
			.is("read_at", null);
		if (error) throw new Error(error.message);
	},

	async moveThreadToTrash(threadId: string, userId: string): Promise<void> {
		const { error } = await supabase
			.from("messages")
			.update({ deleted_at: new Date().toISOString() })
			.eq("thread_id", threadId)
			.eq("recipient_id", userId)
			.is("deleted_at", null);
		if (error) throw new Error(error.message);
	},

	async getMessage(id: string): Promise<InboxMessage | null> {
		const { data, error } = await supabase
			.from("messages")
			.select(MESSAGE_SELECT)
			.eq("id", id)
			.maybeSingle()
			.overrideTypes<MessageJoinRow, { merge: false }>();
		if (error) throw new Error(error.message);
		return data ? toInboxMessage(data) : null;
	},

	async markAsRead(id: string): Promise<void> {
		const { error } = await supabase.from("messages").update({ read_at: new Date().toISOString() }).eq("id", id).is("read_at", null);
		if (error) throw new Error(error.message);
	},

	async moveToTrash(id: string): Promise<void> {
		const { error } = await supabase.from("messages").update({ deleted_at: new Date().toISOString() }).eq("id", id);
		if (error) throw new Error(error.message);
	},

	async restoreFromTrash(id: string): Promise<void> {
		const { error } = await supabase.from("messages").update({ deleted_at: null }).eq("id", id);
		if (error) throw new Error(error.message);
	},

	async countUnread(userId: string): Promise<number> {
		const { count, error } = await supabase
			.from("messages")
			.select("*", { count: "exact", head: true })
			.eq("recipient_id", userId)
			.is("read_at", null)
			.is("deleted_at", null);
		if (error) throw new Error(error.message);
		return count ?? 0;
	},

	async listStaffForStudent(studentSchoolIds: readonly string[]): Promise<PersonPickerOption[]> {
		if (studentSchoolIds.length === 0) return [];
		type StaffJoinRow = {
			school_id: string;
			staff:
				| (Pick<MessagePersonRow, "id" | "first_name" | "last_name" | "email" | "role"> & {
						status: UserStatus;
				  })
				| null;
			school: { name: string | null } | null;
		};
		const { data, error } = await supabase
			.from("staff_schools")
			.select("school_id, staff:profiles ( id, first_name, last_name, email, role, status ), school:schools ( name )")
			.in("school_id", studentSchoolIds as string[])
			.overrideTypes<StaffJoinRow[], { merge: false }>();
		if (error) throw new Error(error.message);
		const seen = new Set<string>();
		const options: PersonPickerOption[] = [];
		for (const row of data ?? []) {
			const s = row.staff;
			if (!s) continue;
			if (s.status !== UserStatus.ACTIVE) continue;
			if (!(STAFF_OR_ABOVE as readonly string[]).includes(s.role)) continue;
			if (seen.has(s.id)) continue;
			seen.add(s.id);
			options.push({
				id: s.id,
				name: personLabel(s.first_name, s.last_name, s.email),
				email: s.email,
				schoolName: row.school?.name ?? null,
				profileId: s.id,
			});
		}
		return options.sort((a, b) => a.name.localeCompare(b.name));
	},

	async listStudentsForStaff(schoolIds: readonly string[] | null): Promise<PersonPickerOption[]> {
		type StudentRow = {
			id: string;
			first_name: string | null;
			last_name: string | null;
			email: string | null;
			school_id: string;
			school: { id: string; name: string | null } | null;
			profile: { id: string; status: UserStatus } | null;
		};
		let query = supabase
			.from("students")
			.select(
				"id, first_name, last_name, email, school_id, school:schools ( id, name ), profile:profiles!profiles_student_id_fkey ( id, status )",
			)
			.order("last_name", { ascending: true });
		if (schoolIds && schoolIds.length > 0) {
			query = query.in("school_id", schoolIds);
		}
		const { data, error } = await query.overrideTypes<StudentRow[], { merge: false }>();
		if (error) throw new Error(error.message);
		return (data ?? [])
			.filter((row) => Boolean(row.email))
			.map((row) => {
				const canMessageInApp = row.profile?.status === UserStatus.ACTIVE;
				return {
					id: row.id,
					name: personLabel(row.first_name, row.last_name, row.email ?? ""),
					email: row.email ?? "",
					schoolName: row.school?.name ?? null,
					profileId: canMessageInApp ? (row.profile?.id ?? null) : null,
				};
			})
			.sort((a, b) => a.name.localeCompare(b.name));
	},
};
